import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { waitlistErrorText } from '../components/booking/waitlistError';
import { useAuth } from '../hooks/useAuth';
import { waitlistApi, type ClaimDetail, type WaitlistApiError } from '../services/waitlistApi';

type Phase = 'loading' | 'unavailable' | 'noSpots' | 'requeued' | 'redirecting';

export default function WaitlistClaim() {
  const { waitlistId } = useParams<{ waitlistId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation('waitlist');

  const [phase, setPhase] = useState<Phase>('loading');
  const [detail, setDetail] = useState<ClaimDetail | null>(null);
  const [reason, setReason] = useState<
    'cutoff' | 'expired' | 'sessionCancelled' | 'forbidden' | 'notFound' | 'unknown'
  >('unknown');
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Bounce to login if signed out, preserving the deep link.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      const target = `/waitlist/claim/${waitlistId ?? ''}`;
      navigate(`/login?redirect=${encodeURIComponent(target)}`, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, waitlistId]);

  // Once authenticated, fetch the claim detail and decide how to proceed.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !waitlistId) return;
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) {
          if (!cancelled) {
            setPhase('unavailable');
            setReason('forbidden');
          }
          return;
        }
        const data = await waitlistApi.getClaim(token, waitlistId);
        if (cancelled) return;

        setDetail(data);

        if (!data.canClaim) {
          if (data.session.isCancelled) {
            setPhase('unavailable');
            setReason('sessionCancelled');
          } else if (data.isWithinCutoff) {
            setPhase('unavailable');
            setReason('cutoff');
          } else if (data.status === 'EXPIRED') {
            setPhase('unavailable');
            setReason('expired');
          } else if (data.status === 'WAITING' && data.wasNotified && data.activityActive) {
            // PRD §3.3d: entry was requeued to WAITING after its notified
            // spot was taken.
            if (data.session.freeSpots > 0) {
              // d2: spots are open right now — go book on the activity page,
              // with the target session preselected.
              setPhase('redirecting');
              navigate(`/activity/${data.activity.slug}?sessionId=${data.session.id}`, {
                replace: true,
              });
            } else {
              // d1: still full — show queue position, next email will come.
              setPhase('requeued');
            }
          } else {
            setPhase('unavailable');
            setReason('unknown');
          }
          return;
        }

        // canClaim is true — branch on freeSpots vs capacityDemand
        if (data.session.freeSpots <= 0) {
          setPhase('noSpots');
        } else {
          setPhase('redirecting');
          const params = new URLSearchParams({
            sessionId: data.session.id,
            waitlistId: data.waitlistId,
          });
          if (data.session.freeSpots >= data.capacityDemand) {
            // Branch (a): direct to checkout with locked party size.
            navigate(`/booking/${data.activity.slug}?${params.toString()}`, { replace: true });
          } else {
            // Branch (b): bounce back to activity detail with the partial hint.
            params.set('waitlistHint', 'partial');
            navigate(`/activity/${data.activity.slug}?${params.toString()}`, { replace: true });
          }
        }
      } catch (err) {
        if (cancelled) return;
        const e = err as WaitlistApiError;
        setPhase('unavailable');
        if (e.status === 404) setReason('notFound');
        else if (e.status === 403) setReason('forbidden');
        else setReason('unknown');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, waitlistId, navigate]);

  const handleLeave = async () => {
    if (!waitlistId) return;
    setBusy(true);
    setActionError(null);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        setActionError(t('signInFirst'));
        setBusy(false);
        return;
      }
      await waitlistApi.leave(token, waitlistId);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setActionError(waitlistErrorText(err, i18n, 'claim.actionFailed'));
      setBusy(false);
    }
  };

  if (phase === 'loading' || phase === 'redirecting' || authLoading) {
    return (
      <div className="min-h-screen bg-warm-white py-16 px-4">
        <div className="mx-auto max-w-lg rounded-xl bg-white p-8 shadow-sm">
          <div className="text-center text-charcoal/70">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (phase === 'noSpots') {
    return (
      <div className="min-h-screen bg-warm-white py-16 px-4">
        <div className="mx-auto max-w-lg rounded-xl bg-white p-8 shadow-sm">
          <h1 className="mb-4 text-xl font-semibold text-charcoal">{t('claim.noSpotsTitle')}</h1>
          <p className="mb-6 text-charcoal/80">{t('claim.noSpotsBody')}</p>
          {actionError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {actionError}
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              disabled={busy}
              className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              type="button"
            >
              {t('claim.stay')}
            </button>
            <button
              onClick={handleLeave}
              disabled={busy}
              className="flex-1 rounded-lg border border-soft-gray px-4 py-2 text-sm font-medium text-charcoal hover:bg-soft-gray/50 disabled:opacity-50"
              type="button"
            >
              {busy ? t('working') : t('claim.leave')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'requeued' && detail) {
    // PRD §3.3d1: requeued entry, session still full. Read-only info page.
    const ordinal = detail.position + 1;
    return (
      <div className="min-h-screen bg-warm-white py-16 px-4">
        <div className="mx-auto max-w-lg rounded-xl bg-white p-8 shadow-sm">
          <h1 className="mb-4 text-xl font-semibold text-charcoal">{t('claim.requeuedTitle')}</h1>
          <p className="mb-6 text-charcoal/80">{t('claim.requeuedBody', { position: ordinal })}</p>
          <button
            onClick={() =>
              navigate(`/activity/${detail.activity.slug}?sessionId=${detail.session.id}`)
            }
            className="mr-3 rounded-lg border border-soft-gray px-4 py-2 text-sm font-medium text-charcoal hover:bg-soft-gray/50"
            type="button"
          >
            {t('viewActivity')}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
            type="button"
          >
            {t('backToMyHome')}
          </button>
        </div>
      </div>
    );
  }

  // phase === 'unavailable'

  return (
    <div className="min-h-screen bg-warm-white py-16 px-4">
      <div className="mx-auto max-w-lg rounded-xl bg-white p-8 shadow-sm">
        <h1 className="mb-4 text-xl font-semibold text-charcoal">{t('claim.unavailableTitle')}</h1>
        <p className="mb-6 text-charcoal/80">{t(`claim.reasons.${reason}`)}</p>
        {detail?.activity?.slug && (
          <button
            onClick={() => navigate(`/activity/${detail.activity.slug}`)}
            className="mr-3 rounded-lg border border-soft-gray px-4 py-2 text-sm font-medium text-charcoal hover:bg-soft-gray/50"
            type="button"
          >
            {t('viewActivity')}
          </button>
        )}
        <button
          onClick={() => navigate('/dashboard')}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
          type="button"
        >
          {t('backToMyHome')}
        </button>
      </div>
    </div>
  );
}
